// ─── Currency & Formatting Utilities ─────────────────────────────────────────
// DRY: thin, currency-aware wrappers over the single source of truth in
// `@bbpos/utils`. Exported names/signatures are preserved so the ~396 existing
// `@/utils/currency` import sites compile unchanged. Every helper accepts an
// optional `CurrencyCode` that defaults to `'INR'`, so INR output stays
// byte-identical (golden-tested) while non-INR tenants get correct subunit math
// from `@bbpos/utils` (e.g. OMR/BHD/KWD are 3-decimal currencies).

import { amountToMajor, formatAmount, getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';

/**
 * Derive a currency's display symbol from `@bbpos/utils` without duplicating the
 * symbol table here. `formatAmount` is defined as `symbol + amountToMajor`, so
 * stripping the formatted-amount suffix yields exactly the symbol the rest of
 * the codebase uses (`₹` for INR, ISO code fallbacks for the GCC set).
 */
function currencySymbol(currency: CurrencyCode): string {
  const amount = amountToMajor(0, currency);
  return formatAmount(0, currency).slice(0, -amount.length);
}

/**
 * Converts the smallest unit (paise for INR) to a formatted string with the
 * currency symbol. Defaults to INR.
 *
 * Delegates to `@bbpos/utils#formatAmount`; the INR path is byte-identical to
 * the legacy `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
 * implementation. No independent `/ 100` logic remains.
 */
export function formatPaise(paise: number, currency: CurrencyCode = 'INR'): string {
  return formatAmount(paise, currency);
}

/**
 * Format a minor-unit amount with the currency symbol. Defaults to INR.
 *
 * Canonical `@/utils/currency` entry point for the ~19 admin call sites that
 * currently import the legacy symbol-arg `formatCurrency` from `@bbpos/utils`
 * (deprecated). The INR path is byte-identical to that legacy default-symbol
 * form (`₹${paiseToRupees(paise)}` === `formatAmount(paise, 'INR')`); non-INR
 * tenants get correct subunit math. Signature mirrors the POS `lib/format.ts`
 * `formatCurrency` so the two frontends migrate with the same recipe. Behaves
 * identically to {@link formatPaise} — kept as a distinct named export purely
 * so downstream prompts can swap the import path without renaming call sites.
 */
export function formatCurrency(paise: number, currency: CurrencyCode = 'INR'): string {
  return formatAmount(paise, currency);
}

/**
 * Short-form currency for compact displays (e.g. stat cards).
 *
 * INR keeps the existing Indian lakh (L) / thousand (K) compaction *exactly*
 * (byte-identical). Non-INR currencies use a generic K/M compaction with the
 * currency symbol. The subunit divisor is sourced from `@bbpos/utils` rather
 * than a hardcoded `/ 100`, so 3-decimal currencies compact correctly.
 */
export function formatPaiseCompact(paise: number, currency: CurrencyCode = 'INR'): string {
  const major = paise / getSubunitMultiplier(currency);

  if (currency === 'INR') {
    // Byte-identical to the legacy implementation (subunit multiplier is 100).
    if (major >= 10_00_000) return `₹${(major / 100_000).toFixed(1)}L`;
    if (major >= 1_000) return `₹${(major / 1_000).toFixed(1)}K`;
    return formatPaise(paise, currency);
  }

  const symbol = currencySymbol(currency);
  if (major >= 1_000_000) return `${symbol}${(major / 1_000_000).toFixed(1)}M`;
  if (major >= 1_000) return `${symbol}${(major / 1_000).toFixed(1)}K`;
  return formatPaise(paise, currency);
}

/**
 * Formats a plain count/number with Indian locale grouping.
 *
 * This is a count formatter (stock quantities, transaction counts), NOT money —
 * it deliberately does not divide by the subunit multiplier. The `currency`
 * parameter is accepted for signature consistency with the money helpers; the
 * in-scope currency set (INR + GCC) all render acceptably with `en-IN` grouping,
 * and INR output stays byte-identical.
 */
export function formatNumber(n: number, _currency: CurrencyCode = 'INR'): string {
  return Number(n).toLocaleString('en-IN');
}

/**
 * Calculates margin percentage between selling price and cost price (both in
 * the smallest unit). Currency-agnostic ratio — unchanged.
 */
export function marginPercent(price: number, cost: number): string {
  if (!price || price <= 0) return '0.0';
  return (((price - cost) / price) * 100).toFixed(1);
}
